import {
  FeedbackClient,
  useFeedbackPrompt
} from "./chunk-KRQLAENM.js";

// src/ui/FeedbackGate.tsx
import { createContext, useCallback as useCallback2, useContext, useEffect as useEffect4, useMemo, useState as useState5 } from "react";

// src/fetchers/defaultFetcher.ts
var defaultFetcher = async (url, { method, body }) => {
  const response = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: body === void 0 ? void 0 : { "Content-Type": "application/json" },
    body: body === void 0 ? void 0 : JSON.stringify(body)
  });
  if (!response.ok) {
    const error = new Error(`Feedback request failed: ${response.status}`);
    error.status = response.status;
    error.body = await response.json().catch(() => void 0);
    throw error;
  }
  return response.status === 204 ? null : response.json().catch(() => null);
};

// src/ui/ErrorBoundary.tsx
import React from "react";
var ErrorBoundary = class extends React.Component {
  constructor() {
    super(...arguments);
    this.state = { caught: false };
  }
  static getDerivedStateFromError() {
    return { caught: true };
  }
  componentDidCatch(error, info) {
    this.props.onError(error, info);
  }
  render() {
    if (this.state.caught) return null;
    return this.props.children;
  }
};

// src/ui/FeedbackDialog.tsx
import { useCallback, useId, useRef } from "react";
import { createPortal } from "react-dom";

// src/ui/theme/sheet.ts
var STYLESHEET = '.hgn-root {\n  --hgn-bg: #ffffff;\n  --hgn-surface: #ffffff;\n  --hgn-surface-raised: #f4f7fc;\n  --hgn-text: #141a24;\n  --hgn-text-muted: #566378;\n  --hgn-border: rgba(20, 34, 62, 0.14);\n  --hgn-accent: #1d6ae0;\n  --hgn-accent-text: #ffffff;\n  --hgn-accent-hover: #1857b8;\n  --hgn-accent-wash: rgba(29, 106, 224, 0.07);\n  --hgn-danger: #b42318;\n  --hgn-ok: #359e74;\n  --hgn-ok-ink: #141a24;\n  --hgn-focus-ring: rgba(29, 106, 224, 0.45);\n  --hgn-backdrop: rgba(16, 24, 40, 0.44);\n  --hgn-radius: 16px;\n  --hgn-radius-sm: 8px;\n  --hgn-shadow: 0 24px 64px rgba(16, 24, 40, 0.16);\n  --hgn-band-1: #b42318;\n  --hgn-band-2: #c2410c;\n  --hgn-band-3: #a16207;\n  --hgn-band-4: #4d7c0f;\n  --hgn-band-5: #15803d;\n  --hgn-band-ink: #ffffff;\n  --hgn-font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;\n  font-family: var(--hgn-font);\n  color: var(--hgn-text);\n}\n.hgn-root[data-hgn-theme=dark] {\n  --hgn-bg: #131d33;\n  --hgn-surface: #1a2740;\n  --hgn-surface-raised: #223150;\n  --hgn-text: #e7ecf5;\n  --hgn-text-muted: #9aa8c2;\n  --hgn-border: rgba(150, 180, 230, 0.2);\n  --hgn-accent: #4da6ff;\n  --hgn-accent-text: #08111f;\n  --hgn-accent-hover: #7cbcff;\n  --hgn-accent-wash: rgba(77, 166, 255, 0.14);\n  --hgn-danger: #ff9b91;\n  --hgn-ok: #4fc79a;\n  --hgn-ok-ink: #08111f;\n  --hgn-focus-ring: rgba(77, 166, 255, 0.55);\n  --hgn-backdrop: rgba(5, 11, 24, 0.66);\n  --hgn-radius: 16px;\n  --hgn-radius-sm: 8px;\n  --hgn-shadow: 0 24px 64px rgba(0, 0, 0, 0.55);\n  --hgn-band-1: #ff9b91;\n  --hgn-band-2: #fdba74;\n  --hgn-band-3: #fcd34d;\n  --hgn-band-4: #a3e635;\n  --hgn-band-5: #4fc79a;\n  --hgn-band-ink: #08111f;\n  --hgn-font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;\n}\n.hgn-root *,\n.hgn-root *::before,\n.hgn-root *::after {\n  box-sizing: border-box;\n}\n.hgn-root :focus-visible {\n  outline: 2px solid var(--hgn-accent);\n  outline-offset: 2px;\n}\n.hgn-root p,\n.hgn-root h2,\n.hgn-root label,\n.hgn-root span {\n  margin: 0;\n  padding: 0;\n}\n.hgn-root ::selection {\n  background: var(--hgn-accent-wash);\n  color: var(--hgn-text);\n}\n\n.hgn-dialog {\n  width: calc(100% - 48px);\n  max-width: 480px;\n  max-height: calc(100vh - 48px);\n  overflow-y: auto;\n  padding: 26px 26px 24px;\n  background: var(--hgn-surface);\n  color: var(--hgn-text);\n  border: 1px solid var(--hgn-border);\n  border-radius: var(--hgn-radius);\n  box-shadow: var(--hgn-shadow);\n  animation: hgn-rise 240ms cubic-bezier(0.16, 1, 0.3, 1);\n  scrollbar-width: thin;\n  scrollbar-color: var(--hgn-border) transparent;\n}\n.hgn-dialog::backdrop {\n  background: var(--hgn-backdrop, rgba(16, 24, 40, 0.44));\n  animation: hgn-fade 180ms ease-out;\n}\n[data-hgn-theme=dark] > .hgn-dialog::backdrop {\n  background: var(--hgn-backdrop, rgba(5, 11, 24, 0.66));\n}\n.hgn-dialog:focus, .hgn-dialog:focus-visible {\n  outline: none;\n}\n.hgn-dialog::-webkit-scrollbar {\n  width: 10px;\n}\n.hgn-dialog::-webkit-scrollbar-track {\n  background: transparent;\n}\n.hgn-dialog::-webkit-scrollbar-thumb {\n  background: var(--hgn-border);\n  border: 3px solid transparent;\n  background-clip: content-box;\n  border-radius: 999px;\n}\n\n.hgn-title {\n  font-size: 1.0625rem;\n  font-weight: 600;\n  letter-spacing: -0.01em;\n  line-height: 1.35;\n}\n\n.hgn-intro {\n  margin-top: 5px;\n  color: var(--hgn-text-muted);\n  font-size: 0.875rem;\n  line-height: 1.55;\n  max-width: 62ch;\n}\n\n.hgn-field {\n  margin-top: 30px;\n}\n\n.hgn-label {\n  display: block;\n  font-size: 0.875rem;\n  font-weight: 500;\n  line-height: 1.45;\n  margin-bottom: 12px;\n}\n\n.hgn-help {\n  margin-top: 6px;\n  color: var(--hgn-text-muted);\n  font-size: 0.8125rem;\n}\n\n.hgn-required {\n  color: var(--hgn-danger);\n  margin-left: 3px;\n  font-weight: 600;\n  cursor: help;\n}\n\n.hgn-error {\n  margin-top: 8px;\n  color: var(--hgn-danger);\n  font-size: 0.8125rem;\n}\n\n.hgn-input,\n.hgn-textarea,\n.hgn-select {\n  font-family: inherit;\n  font-size: 0.875rem;\n  line-height: 1.55;\n  width: 100%;\n  color: var(--hgn-text);\n  caret-color: var(--hgn-accent);\n  background: var(--hgn-surface);\n  border: 1px solid var(--hgn-border);\n  border-radius: var(--hgn-radius-sm);\n  padding: 10px 12px;\n  transition: border-color 150ms ease, box-shadow 150ms ease;\n}\n.hgn-input::placeholder,\n.hgn-textarea::placeholder,\n.hgn-select::placeholder {\n  color: var(--hgn-text-muted);\n}\n.hgn-input:hover,\n.hgn-textarea:hover,\n.hgn-select:hover {\n  border-color: var(--hgn-accent);\n}\n.hgn-input:focus,\n.hgn-textarea:focus,\n.hgn-select:focus {\n  outline: none;\n  border-color: var(--hgn-accent);\n  box-shadow: 0 0 0 2px var(--hgn-focus-ring);\n}\n.hgn-input:focus-visible,\n.hgn-textarea:focus-visible,\n.hgn-select:focus-visible {\n  outline: none;\n}\n\n.hgn-textarea {\n  min-height: 76px;\n  resize: vertical;\n  display: block;\n}\n\n.hgn-scale-head {\n  display: flex;\n  align-items: baseline;\n  justify-content: space-between;\n  gap: 16px;\n  margin-bottom: 12px;\n}\n.hgn-scale-head .hgn-label {\n  margin-bottom: 0;\n}\n\n.hgn-scale {\n  display: flex;\n  gap: 6px;\n}\n\n.hgn-scale-option {\n  flex: 1 1 0;\n  min-width: 0;\n  background: var(--hgn-surface);\n  color: var(--hgn-text);\n  border: 1px solid var(--hgn-border);\n  border-radius: var(--hgn-radius-sm);\n  font-family: inherit;\n  font-size: 0.875rem;\n  font-variant-numeric: tabular-nums;\n  line-height: 1;\n  padding: 13px 0;\n  cursor: pointer;\n  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease, transform 120ms ease;\n}\n.hgn-scale-option[data-band="1"]:hover:not(:disabled):not([aria-checked=true]) {\n  background: var(--hgn-band-1);\n  border-color: var(--hgn-band-1);\n  color: var(--hgn-band-ink);\n}\n.hgn-scale-option[data-band="1"][aria-checked=true] {\n  background: var(--hgn-band-1);\n  border-color: var(--hgn-band-1);\n  color: var(--hgn-band-ink);\n  font-weight: 600;\n}\n.hgn-scale-option[data-band="2"]:hover:not(:disabled):not([aria-checked=true]) {\n  background: var(--hgn-band-2);\n  border-color: var(--hgn-band-2);\n  color: var(--hgn-band-ink);\n}\n.hgn-scale-option[data-band="2"][aria-checked=true] {\n  background: var(--hgn-band-2);\n  border-color: var(--hgn-band-2);\n  color: var(--hgn-band-ink);\n  font-weight: 600;\n}\n.hgn-scale-option[data-band="3"]:hover:not(:disabled):not([aria-checked=true]) {\n  background: var(--hgn-band-3);\n  border-color: var(--hgn-band-3);\n  color: var(--hgn-band-ink);\n}\n.hgn-scale-option[data-band="3"][aria-checked=true] {\n  background: var(--hgn-band-3);\n  border-color: var(--hgn-band-3);\n  color: var(--hgn-band-ink);\n  font-weight: 600;\n}\n.hgn-scale-option[data-band="4"]:hover:not(:disabled):not([aria-checked=true]) {\n  background: var(--hgn-band-4);\n  border-color: var(--hgn-band-4);\n  color: var(--hgn-band-ink);\n}\n.hgn-scale-option[data-band="4"][aria-checked=true] {\n  background: var(--hgn-band-4);\n  border-color: var(--hgn-band-4);\n  color: var(--hgn-band-ink);\n  font-weight: 600;\n}\n.hgn-scale-option[data-band="5"]:hover:not(:disabled):not([aria-checked=true]) {\n  background: var(--hgn-band-5);\n  border-color: var(--hgn-band-5);\n  color: var(--hgn-band-ink);\n}\n.hgn-scale-option[data-band="5"][aria-checked=true] {\n  background: var(--hgn-band-5);\n  border-color: var(--hgn-band-5);\n  color: var(--hgn-band-ink);\n  font-weight: 600;\n}\n.hgn-scale-option:active:not(:disabled) {\n  transform: scale(0.96);\n}\n.hgn-scale-option:focus-visible {\n  outline: none;\n  box-shadow: 0 0 0 3px var(--hgn-focus-ring);\n}\n.hgn-scale-option:disabled {\n  cursor: default;\n  opacity: 0.6;\n}\n\n.hgn-scale-ends {\n  display: flex;\n  justify-content: space-between;\n  gap: 16px;\n  margin-top: 8px;\n  color: var(--hgn-text-muted);\n  font-size: 0.75rem;\n  line-height: 1.4;\n}\n\n.hgn-actions {\n  display: flex;\n  align-items: center;\n  justify-content: flex-end;\n  gap: 8px;\n  margin-top: 30px;\n}\n\n.hgn-button {\n  font-family: inherit;\n  font-size: 0.875rem;\n  font-weight: 500;\n  line-height: 1.2;\n  border: 1px solid transparent;\n  border-radius: var(--hgn-radius-sm);\n  padding: 10px 18px;\n  cursor: pointer;\n  transition: background-color 150ms ease, border-color 150ms ease, transform 100ms ease;\n}\n.hgn-button:active:not(:disabled) {\n  transform: scale(0.97);\n}\n.hgn-button:focus-visible {\n  outline: none;\n  box-shadow: 0 0 0 3px var(--hgn-focus-ring);\n}\n.hgn-button:disabled {\n  opacity: 0.55;\n  cursor: default;\n}\n\n.hgn-button-primary {\n  background: var(--hgn-accent);\n  color: var(--hgn-accent-text);\n}\n.hgn-button-primary:hover:not(:disabled) {\n  background: var(--hgn-accent-hover);\n}\n\n.hgn-button-quiet {\n  background: transparent;\n  color: var(--hgn-text-muted);\n}\n.hgn-button-quiet:hover:not(:disabled) {\n  background: var(--hgn-accent-wash);\n  color: var(--hgn-text);\n}\n\n.hgn-float {\n  position: fixed;\n  right: 20px;\n  bottom: 20px;\n  z-index: var(--hgn-z, 1400);\n  font-family: var(--hgn-font);\n  font-size: 0.875rem;\n  font-weight: 500;\n  color: var(--hgn-accent-text);\n  background: var(--hgn-accent);\n  border: 0;\n  border-radius: 999px;\n  padding: 11px 20px;\n  cursor: pointer;\n  box-shadow: var(--hgn-shadow);\n  transition: background-color 150ms ease, transform 100ms ease;\n}\n.hgn-float:hover {\n  background: var(--hgn-accent-hover);\n}\n.hgn-float:active {\n  transform: scale(0.97);\n}\n.hgn-float:focus-visible {\n  outline: none;\n  box-shadow: 0 0 0 3px var(--hgn-focus-ring);\n}\n\n.hgn-sr-title {\n  position: absolute;\n  width: 1px;\n  height: 1px;\n  overflow: hidden;\n  clip-path: inset(50%);\n  white-space: nowrap;\n}\n\n.hgn-thanks {\n  text-align: center;\n  padding: 12px 4px 4px;\n}\n\n.hgn-thanks-mark {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 52px;\n  height: 52px;\n  border-radius: 999px;\n  background: var(--hgn-ok);\n  color: var(--hgn-ok-ink);\n  margin-bottom: 18px;\n  animation: hgn-pop 320ms cubic-bezier(0.16, 1, 0.3, 1);\n}\n\n.hgn-thanks-title {\n  font-size: 1.0625rem;\n  font-weight: 600;\n  letter-spacing: -0.01em;\n  line-height: 1.35;\n}\n\n.hgn-thanks-body {\n  margin-top: 8px;\n  color: var(--hgn-text-muted);\n  font-size: 0.875rem;\n  line-height: 1.6;\n}\n\n.hgn-thanks-actions {\n  margin-top: 24px;\n}\n\n.hgn-thanks-timer {\n  position: absolute;\n  left: 0;\n  bottom: 0;\n  height: 3px;\n  width: 100%;\n  transform-origin: left center;\n  background: var(--hgn-accent);\n  border-bottom-left-radius: var(--hgn-radius);\n  animation-name: hgn-countdown;\n  animation-timing-function: linear;\n  animation-fill-mode: forwards;\n}\n\n.hgn-alert {\n  margin-bottom: 16px;\n  padding: 10px 12px;\n  border: 1px solid var(--hgn-danger);\n  border-radius: var(--hgn-radius-sm);\n  color: var(--hgn-danger);\n  font-size: 0.8125rem;\n  line-height: 1.5;\n}\n\n@keyframes hgn-fade {\n  from {\n    opacity: 0;\n  }\n}\n@keyframes hgn-rise {\n  from {\n    opacity: 0;\n    transform: translateY(8px) scale(0.985);\n  }\n}\n@keyframes hgn-pop {\n  from {\n    transform: scale(0.86);\n  }\n}\n@keyframes hgn-countdown {\n  from {\n    transform: scaleX(1);\n  }\n  to {\n    transform: scaleX(0);\n  }\n}\n@media (max-width: 400px) {\n  .hgn-dialog {\n    width: calc(100% - 24px);\n    padding: 20px;\n  }\n  .hgn-scale {\n    gap: 4px;\n  }\n  .hgn-scale-option {\n    font-size: 0.8125rem;\n    padding: 12px 0;\n  }\n}\n@media (prefers-reduced-motion: reduce) {\n  .hgn-root *,\n  .hgn-dialog,\n  .hgn-dialog::backdrop {\n    transition: none !important;\n    animation: none !important;\n  }\n  .hgn-thanks-timer {\n    display: none;\n  }\n}';

// src/ui/theme/tokens.ts
function cssVarName(key) {
  return `--hgn-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
}

// src/ui/theme/styles.ts
var STYLE_MARKER = "data-huginn";
function tokensToCss(tokens) {
  return Object.keys(tokens).filter((key) => tokens[key] !== void 0).map((key) => `${cssVarName(key)}: ${tokens[key]};`).join(" ");
}
function buildRootStyle(zIndex, tokens) {
  const overrides = tokensToCss(tokens != null ? tokens : {});
  return zIndex === void 0 ? overrides : `--hgn-z: ${zIndex}; ${overrides}`;
}
function injectStyles() {
  if (typeof document === "undefined") return;
  if (document.querySelector(`style[${STYLE_MARKER}]`)) return;
  const node = document.createElement("style");
  node.setAttribute(STYLE_MARKER, "");
  node.textContent = STYLESHEET;
  document.head.appendChild(node);
}

// src/ui/FeedbackDialog.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
function openModal(dialog) {
  const restoreTo = document.activeElement;
  const native = typeof dialog.showModal === "function";
  if (native) dialog.showModal();
  else dialog.setAttribute("open", "");
  dialog.focus();
  const onFocusIn = (event) => {
    var _a;
    const target = event.target;
    if (target && dialog.contains(target)) return;
    ((_a = dialog.querySelector(FOCUSABLE)) != null ? _a : dialog).focus();
  };
  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      dialog.dispatchEvent(new Event("cancel", { cancelable: true }));
      return;
    }
    if (native || event.key !== "Tab") return;
    const focusable = Array.from(
      dialog.querySelectorAll(FOCUSABLE)
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    }
  };
  const stop = (event) => event.stopPropagation();
  const keyTypes = ["keydown", "keyup", "keypress"];
  if (!native) document.addEventListener("focusin", onFocusIn);
  document.addEventListener("keydown", onKeyDown, true);
  keyTypes.forEach((type) => dialog.addEventListener(type, stop));
  return () => {
    var _a, _b;
    document.removeEventListener("focusin", onFocusIn);
    document.removeEventListener("keydown", onKeyDown, true);
    keyTypes.forEach((type) => dialog.removeEventListener(type, stop));
    (_a = dialog.close) == null ? void 0 : _a.call(dialog);
    if (restoreTo == null ? void 0 : restoreTo.isConnected) (_b = restoreTo.focus) == null ? void 0 : _b.call(restoreTo);
  };
}
function FeedbackDialog({
  theme,
  title,
  intro,
  dismissable,
  onDismiss,
  tokens,
  hideTitle,
  children
}) {
  const titleId = useId();
  const teardown = useRef(null);
  const attach = useCallback((dialog) => {
    var _a;
    (_a = teardown.current) == null ? void 0 : _a.call(teardown);
    teardown.current = dialog && openModal(dialog);
  }, []);
  injectStyles();
  const rootStyle = buildRootStyle(void 0, tokens);
  return createPortal(
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "hgn-root",
        "data-hgn-theme": theme,
        ref: (node) => {
          if (node) node.setAttribute("style", rootStyle);
        },
        children: /* @__PURE__ */ jsxs(
          "dialog",
          {
            className: "hgn-dialog",
            "aria-modal": "true",
            "aria-labelledby": titleId,
            tabIndex: -1,
            ref: attach,
            "data-testid": "FeedbackDialog",
            onCancel: (event) => {
              event.preventDefault();
              if (dismissable) onDismiss == null ? void 0 : onDismiss();
            },
            onClose: (event) => {
              var _a;
              const dialog = event.currentTarget;
              if (dialog.open) return;
              if (dismissable) onDismiss == null ? void 0 : onDismiss();
              else (_a = dialog.showModal) == null ? void 0 : _a.call(dialog);
            },
            children: [
              hideTitle ? /* @__PURE__ */ jsx("span", { className: "hgn-sr-title", id: titleId, children: title }) : /* @__PURE__ */ jsx("h2", { className: "hgn-title", id: titleId, children: title }),
              intro && /* @__PURE__ */ jsx("p", { className: "hgn-intro", children: intro }),
              children
            ]
          }
        )
      }
    ),
    document.body
  );
}

// src/ui/FeedbackThanks.tsx
import { useEffect, useRef as useRef2, useState } from "react";

// src/ui/primitives/Button.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
function Button({ variant, className, ...rest }) {
  return /* @__PURE__ */ jsx2("button", { type: "button", className: `hgn-button hgn-button-${variant} ${className != null ? className : ""}`.trim(), ...rest });
}

// src/ui/FeedbackThanks.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function FeedbackThanks({ title, body, note, closeLabel, onClose, after = 5e3 }) {
  const [paused, setPaused] = useState(false);
  const remaining = useRef2(after);
  const startedAt = useRef2(Date.now());
  useEffect(() => {
    if (paused) return void 0;
    startedAt.current = Date.now();
    const timer = setTimeout(onClose, remaining.current);
    return () => {
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
      clearTimeout(timer);
    };
  }, [paused, onClose]);
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      className: "hgn-thanks",
      onMouseEnter: () => setPaused(true),
      onMouseLeave: () => setPaused(false),
      onFocus: () => setPaused(true),
      onBlur: () => setPaused(false),
      "data-testid": "FeedbackThanks",
      children: [
        /* @__PURE__ */ jsx3("span", { className: "hgn-thanks-mark", "aria-hidden": "true", children: /* @__PURE__ */ jsx3("svg", { viewBox: "0 0 24 24", width: "30", height: "30", fill: "none", "aria-hidden": "true", focusable: "false", children: /* @__PURE__ */ jsx3(
          "path",
          {
            d: "M6 12.6l3.8 3.8L18 8.2",
            stroke: "currentColor",
            strokeWidth: "2.4",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ) }) }),
        /* @__PURE__ */ jsx3("h2", { className: "hgn-thanks-title", children: title }),
        /* @__PURE__ */ jsxs2("p", { className: "hgn-thanks-body", children: [
          body,
          /* @__PURE__ */ jsx3("br", {}),
          note
        ] }),
        /* @__PURE__ */ jsx3("div", { className: "hgn-thanks-actions", children: /* @__PURE__ */ jsx3(Button, { variant: "primary", onClick: onClose, "data-testid": "FeedbackClose", children: closeLabel }) }),
        /* @__PURE__ */ jsx3(
          "span",
          {
            className: "hgn-thanks-timer",
            style: { animationDuration: `${after}ms`, animationPlayState: paused ? "paused" : "running" },
            "aria-hidden": "true"
          }
        )
      ]
    }
  );
}

// src/ui/SchemaForm.tsx
import { useState as useState2 } from "react";
import { withTheme } from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";

// src/ui/isAnswered.ts
function isAnswered(value) {
  if (value === null || value === void 0) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

// src/ui/theme/rjsf.tsx
import React5 from "react";

// src/ui/widgets/describedBy.ts
function describedBy(props) {
  var _a, _b;
  const explicit = props["aria-describedby"];
  if (explicit) return explicit;
  const ids = [];
  if (((_a = props.rawErrors) != null ? _a : []).length > 0) ids.push(`${props.id}__error`);
  if ((_b = props.uiSchema) == null ? void 0 : _b["ui:help"]) ids.push(`${props.id}__help`);
  return ids.length > 0 ? ids.join(" ") : void 0;
}

// src/ui/widgets/CheckboxWidget.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function CheckboxWidget(props) {
  const { id, value, onChange, disabled, readonly, label } = props;
  return /* @__PURE__ */ jsxs3("label", { className: "hgn-label", htmlFor: id, children: [
    /* @__PURE__ */ jsx4(
      "input",
      {
        id,
        type: "checkbox",
        "aria-describedby": describedBy(props),
        checked: Boolean(value),
        disabled: disabled || readonly,
        onChange: (event) => onChange(event.target.checked)
      }
    ),
    " ",
    label
  ] });
}

// src/ui/widgets/DateWidget.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
function DateWidget(props) {
  const { id, value, onChange, disabled, readonly, required } = props;
  return /* @__PURE__ */ jsx5(
    "input",
    {
      id,
      type: "date",
      className: "hgn-input",
      value: value != null ? value : "",
      required,
      disabled,
      readOnly: readonly,
      "aria-describedby": describedBy(props),
      onChange: (event) => onChange(event.target.value === "" ? void 0 : event.target.value)
    }
  );
}

// src/ui/widgets/NumberWidget.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function NumberWidget(props) {
  const { id, value, onChange, disabled, readonly, placeholder, required, schema } = props;
  return /* @__PURE__ */ jsx6(
    "input",
    {
      id,
      type: "number",
      className: "hgn-input",
      value: value === void 0 || value === null ? "" : String(value),
      required,
      disabled,
      readOnly: readonly,
      placeholder,
      min: typeof schema.minimum === "number" ? schema.minimum : void 0,
      max: typeof schema.maximum === "number" ? schema.maximum : void 0,
      "aria-describedby": describedBy(props),
      onChange: (event) => {
        const raw = event.target.value;
        if (raw === "") return onChange(void 0);
        const parsed = Number(raw);
        onChange(Number.isNaN(parsed) ? void 0 : parsed);
      }
    }
  );
}

// src/ui/widgets/RatingWidget.tsx
import { useId as useId2, useRef as useRef3 } from "react";

// src/ui/widgets/bands.ts
var DEFAULT_WORDS = ["Very dissatisfied", "Dissatisfied", "Neutral", "Satisfied", "Very satisfied"];
function bandFor(score, min, max) {
  const span = Math.max(max - min, 1);
  const position = (score - min) / span;
  return Math.min(5, Math.max(1, Math.ceil(position * 5) || 1));
}

// src/ui/widgets/RatingWidget.tsx
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
function RatingWidget(props) {
  var _a, _b;
  const { schema, value, onChange, disabled, readonly, options, label, id, required } = props;
  const groupLabelId = useId2();
  const groupRef = useRef3(null);
  const min = typeof schema.minimum === "number" ? schema.minimum : 1;
  const max = typeof schema.maximum === "number" ? schema.maximum : 10;
  const points = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  const words = DEFAULT_WORDS;
  const locked = disabled || readonly;
  const score = typeof value === "number" ? value : null;
  const wordFor = (point) => {
    var _a2;
    return (_a2 = words[bandFor(point, min, max) - 1]) != null ? _a2 : "";
  };
  const chosen = points.indexOf(score);
  const tabbable = chosen >= 0 ? chosen : 0;
  const select = (index) => {
    var _a2, _b2;
    if (index < 0 || index >= points.length) return;
    onChange(points[index]);
    (_b2 = (_a2 = groupRef.current) == null ? void 0 : _a2.querySelectorAll('[role="radio"]')[index]) == null ? void 0 : _b2.focus();
  };
  const onKeyDown = (event, index) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const delta = step[event.key];
    if (delta === void 0) return;
    event.preventDefault();
    select(index + delta);
  };
  return /* @__PURE__ */ jsxs4("div", { children: [
    /* @__PURE__ */ jsxs4("span", { className: "hgn-label", id: groupLabelId, children: [
      label != null ? label : schema.title,
      required && /* @__PURE__ */ jsx7(RequiredMark, {})
    ] }),
    /* @__PURE__ */ jsx7(
      "div",
      {
        className: "hgn-scale",
        role: "radiogroup",
        "aria-labelledby": groupLabelId,
        "aria-describedby": describedBy(props),
        "aria-required": required || void 0,
        ref: groupRef,
        "data-testid": "FeedbackRating",
        children: points.map((point, index) => /* @__PURE__ */ jsx7(
          "button",
          {
            type: "button",
            role: "radio",
            id: index === 0 ? id : void 0,
            className: "hgn-scale-option",
            "data-band": bandFor(point, min, max),
            "aria-checked": score === point,
            "aria-label": `${point} ${wordFor(point)}`.trim(),
            tabIndex: index === tabbable ? 0 : -1,
            disabled: locked,
            onClick: () => select(index),
            onKeyDown: (event) => onKeyDown(event, index),
            children: point
          },
          point
        ))
      }
    ),
    ((options == null ? void 0 : options.minLabel) || (options == null ? void 0 : options.maxLabel)) && /* @__PURE__ */ jsxs4("div", { className: "hgn-scale-ends", children: [
      /* @__PURE__ */ jsx7("span", { children: (_a = options == null ? void 0 : options.minLabel) != null ? _a : "" }),
      /* @__PURE__ */ jsx7("span", { children: (_b = options == null ? void 0 : options.maxLabel) != null ? _b : "" })
    ] })
  ] });
}

// src/ui/widgets/SelectWidget.tsx
import { jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
function SelectWidget(props) {
  var _a;
  const { id, value, onChange, disabled, readonly, required, options } = props;
  const enumOptions = (_a = options == null ? void 0 : options.enumOptions) != null ? _a : [];
  const blankLabel = props.placeholder || "No answer";
  return /* @__PURE__ */ jsxs5(
    "select",
    {
      id,
      className: "hgn-select",
      "aria-describedby": describedBy(props),
      value: value != null ? value : "",
      required,
      disabled: disabled || readonly,
      onChange: (event) => onChange(event.target.value === "" ? void 0 : event.target.value),
      children: [
        !required && /* @__PURE__ */ jsx8("option", { value: "", children: blankLabel }),
        enumOptions.map((option) => /* @__PURE__ */ jsx8("option", { value: String(option.value), children: option.label }, String(option.value)))
      ]
    }
  );
}

// src/ui/widgets/TextWidget.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
function TextWidget(props) {
  const { id, value, onChange, disabled, readonly, placeholder, required, schema } = props;
  return /* @__PURE__ */ jsx9(
    "input",
    {
      id,
      type: "text",
      className: "hgn-input",
      "aria-describedby": describedBy(props),
      value: value != null ? value : "",
      required,
      disabled,
      readOnly: readonly,
      placeholder,
      maxLength: typeof schema.maxLength === "number" ? schema.maxLength : void 0,
      onChange: (event) => onChange(event.target.value === "" ? void 0 : event.target.value)
    }
  );
}

// src/ui/widgets/TextareaWidget.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
function TextareaWidget(props) {
  const { id, value, onChange, disabled, readonly, placeholder, required, schema } = props;
  return /* @__PURE__ */ jsx10(
    "textarea",
    {
      id,
      className: "hgn-textarea",
      "aria-describedby": describedBy(props),
      value: value != null ? value : "",
      required,
      disabled,
      readOnly: readonly,
      placeholder,
      maxLength: typeof schema.maxLength === "number" ? schema.maxLength : void 0,
      onChange: (event) => onChange(event.target.value === "" ? void 0 : event.target.value)
    }
  );
}

// src/ui/theme/rjsf.tsx
import { Fragment, jsx as jsx11, jsxs as jsxs6 } from "react/jsx-runtime";
var widgets = {
  rating: RatingWidget,
  updown: NumberWidget,
  date: DateWidget,
  TextWidget,
  TextareaWidget,
  SelectWidget,
  CheckboxWidget
};
function RequiredMark() {
  return /* @__PURE__ */ jsx11("span", { className: "hgn-required", "aria-hidden": "true", title: "Required", children: "*" });
}
function FieldTemplate(props) {
  var _a;
  const { id, label, help, description, errors, children, schema, displayLabel, rawErrors } = props;
  const ownsItsLabel = ((_a = props.uiSchema) == null ? void 0 : _a["ui:widget"]) === "rating" || schema.type === "boolean";
  const hasError = (rawErrors != null ? rawErrors : []).length > 0;
  return /* @__PURE__ */ jsxs6("div", { className: "hgn-field", children: [
    displayLabel && !ownsItsLabel && /* @__PURE__ */ jsxs6("label", { className: "hgn-label", htmlFor: id, children: [
      label,
      props.required && /* @__PURE__ */ jsx11(RequiredMark, {})
    ] }),
    description,
    children,
    help && /* @__PURE__ */ jsx11("div", { className: "hgn-help", id: `${id}__help`, children: help }),
    hasError && /* @__PURE__ */ jsx11("div", { className: "hgn-error", id: `${id}__error`, children: errors })
  ] });
}
function ObjectFieldTemplate({ properties, description }) {
  return /* @__PURE__ */ jsxs6(Fragment, { children: [
    description,
    properties.map((property) => /* @__PURE__ */ jsx11(React5.Fragment, { children: property.content }, property.name))
  ] });
}
function ErrorListTemplate() {
  return null;
}
var HUGINN_THEME = {
  widgets,
  templates: { FieldTemplate, ObjectFieldTemplate, ErrorListTemplate }
};

// src/ui/SchemaForm.tsx
import { jsx as jsx12, jsxs as jsxs7 } from "react/jsx-runtime";
var ThemedForm = withTheme(HUGINN_THEME);
function SchemaForm({ schema, uiSchema, formData, onSubmit, submitLabel, submitting, secondary }) {
  const [data, setData] = useState2(formData != null ? formData : {});
  const unanswered = validator.validateFormData(data, schema).errors.some((error) => error.name === "required");
  const blocked = unanswered ? "Answer the required questions first" : Object.values(data).some(isAnswered) ? void 0 : "Answer at least one question first";
  return /* @__PURE__ */ jsx12(
    ThemedForm,
    {
      schema,
      uiSchema,
      formData: data,
      validator,
      showErrorList: false,
      onChange: (event) => {
        var _a;
        return setData((_a = event.formData) != null ? _a : {});
      },
      onSubmit: (event) => {
        var _a;
        return onSubmit((_a = event.formData) != null ? _a : {});
      },
      children: /* @__PURE__ */ jsxs7("div", { className: "hgn-actions", children: [
        secondary,
        /* @__PURE__ */ jsx12(
          Button,
          {
            variant: "primary",
            type: "submit",
            disabled: submitting || Boolean(blocked),
            title: blocked,
            "data-testid": "FeedbackSubmit",
            children: submitting ? "Sending\u2026" : submitLabel
          }
        )
      ] })
    }
  );
}

// src/ui/buildFeedbackContext.ts
function buildFeedbackContext() {
  if (typeof window === "undefined") return {};
  return {
    route: window.location.pathname,
    url: window.location.href,
    user_agent: window.navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`
  };
}

// src/ui/primitives/Alert.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
function Alert({ children }) {
  return /* @__PURE__ */ jsx13("div", { className: "hgn-alert", role: "alert", children });
}

// src/ui/useNavigationKey.ts
import { useEffect as useEffect2, useState as useState3 } from "react";
var KEY = /* @__PURE__ */ Symbol.for("huginn.useNavigationKey.registry");
function registry() {
  const host = globalThis;
  if (!host[KEY]) host[KEY] = { listeners: /* @__PURE__ */ new Set(), natives: null, ours: null };
  return host[KEY];
}
function announce() {
  registry().listeners.forEach((listener) => listener());
}
function patchHistory() {
  const reg = registry();
  if (reg.ours) return;
  reg.natives = { pushState: history.pushState, replaceState: history.replaceState };
  const wrap = (original) => function wrapped(...args) {
    const result = original.apply(this, args);
    announce();
    return result;
  };
  reg.ours = { pushState: wrap(reg.natives.pushState), replaceState: wrap(reg.natives.replaceState) };
  history.pushState = reg.ours.pushState;
  history.replaceState = reg.ours.replaceState;
}
function unpatchHistory() {
  const reg = registry();
  if (!reg.ours || !reg.natives) return;
  if (history.pushState === reg.ours.pushState) history.pushState = reg.natives.pushState;
  if (history.replaceState === reg.ours.replaceState) history.replaceState = reg.natives.replaceState;
  reg.ours = null;
  reg.natives = null;
}
function currentKey() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}
function useNavigationKey() {
  const [key, setKey] = useState3(currentKey);
  useEffect2(() => {
    const listener = () => setKey(currentKey());
    const reg = registry();
    reg.listeners.add(listener);
    patchHistory();
    window.addEventListener("popstate", listener);
    window.addEventListener("hashchange", listener);
    return () => {
      reg.listeners.delete(listener);
      window.removeEventListener("popstate", listener);
      window.removeEventListener("hashchange", listener);
      if (reg.listeners.size === 0) unpatchHistory();
    };
  }, []);
  return key;
}

// src/ui/useResolvedTheme.ts
import { useEffect as useEffect3, useState as useState4 } from "react";
function useResolvedTheme(theme) {
  const [systemDark, setSystemDark] = useState4(false);
  useEffect3(() => {
    if (theme !== "auto" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    setSystemDark(query.matches);
    const onChange = (event) => setSystemDark(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [theme]);
  if (theme === "auto") return systemDark ? "dark" : "light";
  return theme;
}

// src/ui/FeedbackGate.tsx
import { Fragment as Fragment2, jsx as jsx14, jsxs as jsxs8 } from "react/jsx-runtime";
var DEFAULT_LABELS = {
  periodicTitle: "Before you carry on",
  adhocTitle: "Send feedback",
  submit: "Send feedback",
  dismiss: "Not now",
  trigger: "Feedback",
  skip: "Skip this time",
  error: "Could not send your feedback. Please try again.",
  thanksTitle: "Thank you",
  thanksBody: "Your feedback has been sent.",
  thanksNote: "We really appreciate you taking the time.",
  close: "Close"
};
var Trigger = createContext({ open: () => {
}, available: false });
function useFeedbackTrigger() {
  return useContext(Trigger);
}
function FeedbackGate({
  fetcher,
  client,
  apiBase = "/api/feedback",
  theme = "light",
  tokens,
  labels,
  enabled = true,
  pollMs,
  channelName,
  adhocTrigger = "floating",
  zIndex,
  onSubmitted,
  children
}) {
  injectStyles();
  const copy = { ...DEFAULT_LABELS, ...labels };
  const resolvedTheme = useResolvedTheme(theme);
  const locationKey = useNavigationKey();
  const feedbackClient = useMemo(() => {
    if (client) {
      if (fetcher) {
        console.error("[huginn] FeedbackGate was given both `client` and `fetcher`; using `client` and ignoring `fetcher` and `apiBase`.");
      }
      return client;
    }
    return new FeedbackClient({ fetcher: fetcher != null ? fetcher : defaultFetcher, baseUrl: apiBase });
  }, [client, fetcher, apiBase]);
  const { due, mode, cycleId, periodic, adhoc, submit, skip, submitAdhoc, refresh } = useFeedbackPrompt({
    client: feedbackClient,
    locationKey,
    enabled,
    ...pollMs === void 0 ? {} : { pollMs },
    ...channelName === void 0 ? {} : { channelName }
  });
  const [adhocOpen, setAdhocOpen] = useState5(false);
  const [submitting, setSubmitting] = useState5(false);
  const [error, setError] = useState5("");
  const [thanked, setThanked] = useState5(false);
  const [dialogBroken, setDialogBroken] = useState5(false);
  const handleDialogError = useCallback2(
    (kind) => (renderError, info) => {
      console.error(
        `Huginn: the ${kind} feedback form failed to render and has been suppressed for the rest of this session.`,
        renderError,
        info.componentStack
      );
      setDialogBroken(true);
    },
    []
  );
  const openAdhoc = useCallback2(() => {
    setError("");
    setAdhocOpen(true);
  }, []);
  const triggerValue = useMemo(
    () => ({ open: openAdhoc, available: enabled && Boolean(adhoc) }),
    [openAdhoc, enabled, adhoc]
  );
  const send = async (kind, answers) => {
    setSubmitting(true);
    setError("");
    try {
      const context = buildFeedbackContext();
      if (kind === "periodic") {
        await submit(answers, context);
      } else {
        await submitAdhoc(answers, context);
      }
      onSubmitted == null ? void 0 : onSubmitted(kind);
      setThanked(true);
      await refresh();
    } catch (e) {
      setError(copy.error);
    } finally {
      setSubmitting(false);
    }
  };
  const dismissThanks = useCallback2(() => {
    setThanked(false);
    setAdhocOpen(false);
  }, []);
  const mandatory = enabled && due && Boolean(periodic) && cycleId !== void 0 && !thanked;
  useEffect4(() => {
    setError("");
    setAdhocOpen(false);
  }, [mandatory]);
  useEffect4(() => {
    if (!enabled) setThanked(false);
  }, [enabled]);
  const skippable = mode === "optional";
  const onSkip = async () => {
    setSubmitting(true);
    setError("");
    try {
      await skip();
      await refresh();
    } catch (e) {
      setError(copy.error);
    } finally {
      setSubmitting(false);
    }
  };
  const form = (payloadForm, kind, secondary) => /* @__PURE__ */ jsxs8(Fragment2, { children: [
    error && /* @__PURE__ */ jsx14(Alert, { children: error }),
    /* @__PURE__ */ jsx14(
      SchemaForm,
      {
        schema: payloadForm.schema,
        uiSchema: payloadForm.ui_schema,
        onSubmit: (data) => void send(kind, data),
        submitLabel: copy.submit,
        submitting,
        secondary
      },
      kind
    )
  ] });
  return /* @__PURE__ */ jsxs8(Trigger.Provider, { value: triggerValue, children: [
    children,
    enabled && thanked && /* @__PURE__ */ jsx14(
      FeedbackDialog,
      {
        theme: resolvedTheme,
        title: copy.thanksTitle,
        tokens,
        dismissable: true,
        onDismiss: dismissThanks,
        hideTitle: true,
        children: /* @__PURE__ */ jsx14(
          FeedbackThanks,
          {
            title: copy.thanksTitle,
            body: copy.thanksBody,
            note: copy.thanksNote,
            closeLabel: copy.close,
            onClose: dismissThanks
          }
        )
      }
    ),
    mandatory && !dialogBroken && /* @__PURE__ */ jsx14(ErrorBoundary, { onError: handleDialogError("periodic"), children: /* @__PURE__ */ jsx14(
      FeedbackDialog,
      {
        theme: resolvedTheme,
        title: copy.periodicTitle,
        intro: periodic.intro,
        tokens,
        children: form(
          periodic,
          "periodic",
          skippable ? /* @__PURE__ */ jsx14(Button, { variant: "quiet", onClick: () => void onSkip(), disabled: submitting, "data-testid": "FeedbackSkip", children: copy.skip }) : void 0
        )
      }
    ) }),
    !mandatory && !thanked && adhocOpen && adhoc && !dialogBroken && /* @__PURE__ */ jsx14(ErrorBoundary, { onError: handleDialogError("adhoc"), children: /* @__PURE__ */ jsx14(
      FeedbackDialog,
      {
        theme: resolvedTheme,
        title: copy.adhocTitle,
        intro: adhoc.intro,
        tokens,
        dismissable: true,
        onDismiss: () => setAdhocOpen(false),
        children: form(adhoc, "adhoc", /* @__PURE__ */ jsx14(Button, { variant: "quiet", onClick: () => setAdhocOpen(false), children: copy.dismiss }))
      }
    ) }),
    !mandatory && !thanked && adhocTrigger === "floating" && enabled && adhoc && /* @__PURE__ */ jsx14(
      "button",
      {
        type: "button",
        className: "hgn-float hgn-root",
        "data-hgn-theme": resolvedTheme,
        "data-testid": "FeedbackButton",
        onClick: openAdhoc,
        ref: (node) => {
          if (node) node.setAttribute("style", buildRootStyle(zIndex, tokens));
        },
        children: copy.trigger
      }
    )
  ] });
}
export {
  FeedbackGate,
  buildFeedbackContext,
  injectStyles,
  useFeedbackTrigger
};
